// src/App.js
import React, { useEffect, useState } from "react";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";

import Navbar from "./components/Navbar/Navbar";
import { fetchKundliFromBirthData } from "./services/kundliService";
import { useKundli } from "./context/KundliContext";
import { useAuth } from "./context/AuthContext";

import "./App.css";

const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const PrivacyPolicy = React.lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsOfService = React.lazy(() => import("./pages/legal/TermsOfService"));
const RefundPolicy = React.lazy(() => import("./pages/legal/RefundPolicy"));
const Disclaimer = React.lazy(() => import("./pages/legal/Disclaimer"));
const ContactUs = React.lazy(() => import("./pages/legal/ContactUs"));
const DocumentationPage = React.lazy(() => import("./pages/DocumentationPage"));
const VedicAstrologyGuide = React.lazy(() => import("./pages/vedicAstrologyGuide/VedicAstrologyGuide"));
const BhriguNandiNadiGuide = React.lazy(() => import("./pages/bhriguNandiNadiGuide/BhriguNandiNadiGuide"));
const NakshatraGuide = React.lazy(() => import("./pages/nakshatraGuide/NakshatraGuide"));
const NumerologyGuide = React.lazy(() => import("./pages/numerologyGuide/NumerologyGuide"));
const ComprehensiveDashboard = React.lazy(() => import("./pages/ComprehensiveDashboard"));
const SavedChartsPage = React.lazy(() => import("./pages/SavedChartsPage"));

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]);

  return null;
};

const ProtectedRoute = ({ children }) => {
  const { currentUser, isPremium } = useAuth();
  if (!currentUser) {
    return <Navigate to="/" replace />;
  }
  if (!isPremium) {
    return <Navigate to="/" replace state={{ showUpsell: true }} />;
  }
  return children;
};

const App = () => {
  const { kundliData, setKundliData, kundliChartData, setKundliChartData } = useKundli();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const navigate  = useNavigate();
  const location  = useLocation();
  const isLanding = location.pathname === "/" || location.pathname.startsWith("/privacy") || location.pathname.startsWith("/terms") || location.pathname.startsWith("/refund") || location.pathname.startsWith("/disclaimer") || location.pathname.startsWith("/contact") || location.pathname.startsWith("/docs") || location.pathname.startsWith("/guide") || location.pathname.startsWith("/bnn-guide") || location.pathname.startsWith("/nakshatra-guide") || location.pathname.startsWith("/numerology-guide");

  useEffect(() => {
    // If not authenticated and trying to access a protected route, redirect to landing
    if (!currentUser && !isLanding) {
      navigate("/");
      setLoading(false);
      return;
    }

    const loadKundli = async () => {
      const stored   = localStorage.getItem("kundliData");
      const formData = stored ? JSON.parse(stored) : null;

      // If user is authenticated but has no kundli data, we might want them to fill it out
      if (!formData || !formData.dob || !formData.time || !formData.lat || !formData.lon) {
        setLoading(false);
        // Do not force redirect if they are logged in. The dashboard can handle showing Kundli form or empty state.
        return;
      }

      try {
        setFetchError(null);
        const kundli = await fetchKundliFromBirthData({
          date: formData.dob,
          time: formData.time,
          lat: formData.lat,
          lon: formData.lon,
        });
        setKundliData({ ...formData });
        setKundliChartData(kundli);
        localStorage.setItem("kundliChartData", JSON.stringify(kundli));

        // Auto-navigate removed so users can see the landing page even if logged in
        // if (isLanding && currentUser) navigate("/dashboard");
      } catch (error) {
        console.error("❌ Failed to fetch kundli data", error);
        setFetchError("Failed to generate birth chart. Please try again or check your connection.");
      } finally {
        setLoading(false);
      }
    };

    const needsRefresh = !kundliChartData || !kundliChartData.shadbala || !kundliChartData.chalitChart;
    if (!kundliData || needsRefresh) {
      loadKundli();
    } else {
      setLoading(false);
      // Auto-navigate removed so users can see the landing page even if logged in
      // if (isLanding && currentUser) navigate("/dashboard");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isLanding]);

  return (
    <div className={`app-container${isLanding ? " is-page" : ""}`} style={{ flexDirection: "column" }}>
      <ScrollToTop />
      {/* Global SVG Definitions for gradients used by planetary colors */}
      <svg width="0" height="0" style={{ position: 'absolute', pointerEvents: 'none' }}>
        <defs>
          <linearGradient id="rainbowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="red" />
            <stop offset="16%" stopColor="orange" />
            <stop offset="33%" stopColor="yellow" />
            <stop offset="50%" stopColor="green" />
            <stop offset="66%" stopColor="blue" />
            <stop offset="83%" stopColor="indigo" />
            <stop offset="100%" stopColor="violet" />
          </linearGradient>
        </defs>
      </svg>

      {/* Navbar only shown on dashboard (landing has its own nav) */}
      {!isLanding && <Navbar />}

      {!isLanding && loading ? (
        <div className="kundli-loading">Loading Cosmic Data…</div>
      ) : (
        <React.Suspense fallback={<div className="kundli-loading">Loading Cosmic Data…</div>}>
          <Routes>
            <Route path="/"          element={<LandingPage />} />
            <Route path="/privacy-policy"   element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/refund-policy"    element={<RefundPolicy />} />
            <Route path="/disclaimer"       element={<Disclaimer />} />
            <Route path="/contact"          element={<ContactUs />} />
            <Route path="/docs"             element={<DocumentationPage />} />
            <Route path="/guide"            element={<VedicAstrologyGuide />} />
            <Route path="/bhrigu-nandi-nadi-guide" element={<BhriguNandiNadiGuide />} />
            <Route path="/bnn-guide"        element={<BhriguNandiNadiGuide />} />
            <Route path="/nakshatra-guide"  element={<NakshatraGuide />} />
            <Route path="/numerology-guide" element={<NumerologyGuide />} />
            <Route path="/dashboard" element={
              <ProtectedRoute>
                {fetchError ? (
                  <div className="dashboard-setup-container" style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                    <h2 style={{ color: '#ff4d4d' }}>Error Generating Chart</h2>
                    <p>{fetchError}</p>
                    <button className="kf-submit" onClick={() => window.location.reload()} style={{ marginTop: '1rem', width: 'auto' }}>
                      Retry
                    </button>
                  </div>
                ) : (
                  <ComprehensiveDashboard />
                )}
              </ProtectedRoute>
            } />
            <Route path="/saved-charts"     element={<ProtectedRoute><SavedChartsPage /></ProtectedRoute>} />
            


            <Route path="*"          element={<LandingPage />} />
            </Routes>
          </React.Suspense>
      )}
    </div>
  );
};

export default App;
